import PropTypes from 'prop-types';
import { useId } from '@reach/auto-id';

import Checkbox from '../checkbox';
import SimpleField from '../simple_field';

/**
 * Renders a checkbox field. Supports checked, unchecked and mixed states.
 */
export default function CheckboxField({
  className,
  frame,
  hideRequired,
  hint,
  id,
  label,
  size,
  ...otherProps
}) {
  const idWithDefault = useId(id);

  const { disabled, required } = otherProps;

  return (
    <SimpleField
      className={className}
      disabled={disabled}
      frame={frame}
      hideRequired={hideRequired}
      hint={hint}
      id={idWithDefault}
      label={label}
      required={required}
      size={size}
    >
      <Checkbox id={idWithDefault} {...otherProps} />
    </SimpleField>
  );
}

CheckboxField.propTypes = {
  /**
   * ARIA label.
   */
  ariaLabel: PropTypes.string,
  /**
   * Value. Use for controlled checkboxes along with `onChange`.
   */
  checked: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(['mixed'])]),
  /**
   * Field class name.
   */
  className: PropTypes.string,
  /**
   * Default checked. Use for uncontrolled inputs.
   */
  defaultChecked: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(['mixed'])]),
  /**
   * Whether to disable the input.
   */
  disabled: PropTypes.bool,
  /**
   * Whether to render the field as a frame.
   */
  frame: PropTypes.bool,
  /**
   * Whether to hide "Required" label despite field being required.
   */
  hideRequired: PropTypes.bool,
  /**
   * Input hint. Displayed below the input.
   */
  hint: PropTypes.node,
  /**
   * Input ID. If not provided, ID will be automatically generated.
   */
  id: PropTypes.string,
  /**
   * Input label. Displayed above the input.
   */
  label: PropTypes.node,
  /**
   * Input name.
   */
  name: PropTypes.string.isRequired,
  /**
   * Function called on change. Will be called with two attributes: `checked` and `event`.
   */
  onChange: PropTypes.func,
  /**
   * Whether the input should be required.
   */
  required: PropTypes.bool,
  /**
   * Checkbox size.
   */
  size: PropTypes.oneOf(['regular', 'small']),
  /**
   * Checkbox value.
   */
  value: PropTypes.string,
};
